import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Lock, Download } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, startCheckout, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Body } from "@/components/ArticleBody";

const tiersLabel = (t = []) => (t.includes("pro") ? "Included free with Pro & Institutional" : "Included free with Institutional");

const SourcesList = ({ items }) => (
  <div className="mt-10 border-t-2 border-[#0A0A0A] pt-5" data-testid="report-sources">
    <p className="overline text-[#575757]">Sources ({items.length})</p>
    <ol className="mt-3 space-y-2 text-sm">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3"><span className="font-mono-d text-[#E5332A]">{String(i + 1).padStart(2, "0")}</span>
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#E5332A] hover:underline">{s.title} <span className="text-[#575757]">· {s.source}{s.published ? ` · ${fmtDate(s.published)}` : ""}</span></a></li>
      ))}
    </ol>
  </div>
);

const BuyBox = ({ r, busy, onBuy, onDownload }) => (
  <div className="lg:sticky lg:top-36 border border-[#E7E7E7] bg-white">
    <img src={r.image} alt={r.title} className="w-full aspect-[16/10] object-cover" />
    <div className="p-6">
      <p className="font-display text-4xl font-bold" data-testid="report-detail-price">{gbp(r.price)}</p>
      <p className="text-xs text-[#575757] mt-1">{r.pages} pages · PDF · lifetime access</p>
      <ul className="mt-6 space-y-2 text-sm">
        {[r.kind === "monthly" ? "In-depth trade & investment analysis" : "Full report with data tables", "Printable PDF edition", tiersLabel(r.included_tiers)].map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-[#E5332A] shrink-0" /> {f}</li>)}
      </ul>
      {r.owned ? (
        <button onClick={onDownload} data-testid="report-download-btn" className="mt-6 w-full flex items-center justify-center gap-2 bg-[#0A0A0A] text-white py-3 text-sm hover:bg-[#333333] transition-colors"><Download size={16} /> Download PDF</button>
      ) : (
        <>
          <button onClick={onBuy} disabled={busy} data-testid="report-buy-btn" className="mt-6 w-full bg-[#E5332A] text-white py-3 text-sm hover:bg-[#B8241C] transition-colors">{busy ? "Opening checkout…" : `Buy report · ${gbp(r.price)}`}</button>
          <Link to="/pricing" data-testid="report-institutional-link" className="mt-3 block text-center text-sm border border-[#0A0A0A] py-3 hover:bg-[#0A0A0A] hover:text-white transition-colors">{r.included_tiers?.includes("pro") ? "Get it free with Pro" : "Get all reports with Institutional"}</Link>
        </>
      )}
    </div>
  </div>
);

export default function ReportDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { api.get(`/reports/${slug}`).then((res) => setR(res.data)); }, [slug, user]);

  const buy = async () => {
    if (!user) return navigate(`/login?next=/research/${slug}`);
    setBusy(true);
    try { await startCheckout(`report_${slug}`); } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  const download = async () => {
    try {
      const res = await api.get(`/reports/${slug}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${slug}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed. Please try again."); }
  };
  if (!r) return <div className="min-h-[70vh]" />;
  const hasBody = r.body?.length > 0;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-12 gap-12" data-testid="report-detail-page">
      <div className="lg:col-span-7 min-w-0">
        <Link to="/research" className="overline text-[#E5332A]">← Research store</Link>
        {r.kind === "monthly" && <p className="overline text-[#575757] mt-6" data-testid="report-monthly-label">Monthly Report · {fmtDate(r.published_at)} · {r.source_count} sources</p>}
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-4 leading-[1.08]" data-testid="report-title">{r.title}</h1>
        <p className="text-base md:text-lg text-[#575757] mt-5 leading-relaxed">{r.summary}</p>
        <div className="mt-10">
          <p className="overline text-[#575757]">Contents</p>
          <ol className="mt-4 divide-y divide-[#E7E7E7] border-y border-[#E7E7E7]">
            {r.toc.map((t, i) => <li key={t} className="py-3 flex gap-4 text-sm"><span className="font-mono-d text-[#E5332A]">{String(i + 1).padStart(2, "0")}</span>{t}</li>)}
          </ol>
        </div>
        <div className="mt-10 bg-white border border-[#E7E7E7] p-6 sm:p-8" data-testid="report-content">
          <p className="overline text-[#E5332A]">{r.owned ? "Full report" : "Executive summary preview"}</p>
          {hasBody ? <Body paras={r.body} testid="report-body" /> : r.content.map((c, i) => <p key={i} className="mt-4 leading-relaxed">{c}</p>)}
          {!r.owned && <p className="mt-6 flex items-center gap-2 text-sm text-[#575757]" data-testid="report-locked-note"><Lock size={14} /> Buy the report or join {r.included_tiers?.includes("pro") ? "Pro" : "Institutional"} to unlock all {r.pages} pages.</p>}
          {r.owned && r.sources?.length > 0 && <SourcesList items={r.sources} />}
        </div>
      </div>
      <aside className="lg:col-span-4 lg:col-start-9"><BuyBox r={r} busy={busy} onBuy={buy} onDownload={download} /></aside>
    </div>
  );
}
